import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { BaseComponent } from 'src/app/moov-common/base/base.component';
import { BatchId, DiscoveryChatQuestion, DiscoveryResponse, PossibleResponse } from '../model/discovery-chat.interface';
import { AbstractDiscoveryProcessService } from '../discovery-process.abstract.service';
import { firstValueFrom, forkJoin } from 'rxjs';
import { sortBy } from 'lodash';
import { getDiscoveryProgressValue } from '../discovery-progress';
import { Router } from '@angular/router';
import { AbstractAuthorizationService } from 'src/app/moov-common/services/authorization/authorization.abstract.service';
import { DoneDiscoveryProcessComponent } from '../done-discovery-process/done-discovery-process.component';
import { NAVIGATION_ROUTES } from 'src/app/navigation-routes.const';
import { DiscoveryStatusEnum } from 'src/app/moov-common/services/personal-user-data/personal-user-data';
import { ResponseStateEnum, DiscoveryQuestionTypeEnum } from '../discovery-process.enum';
import { AbstractPersonalUserDataService } from 'src/app/moov-common/services/personal-user-data/personal-user-data.abstract.service';
import { FeatureID } from 'src/app/moov-common/enum/feature.enum';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { GenderPipe } from 'src/app/moov-common/pipes/gender.pipe';

@Component({
  selector: 'app-discovery-wizard',
  templateUrl: './discovery-wizard.component.html',
  styleUrls: ['./discovery-wizard.component.scss'],
})

export class DiscoveryWizardComponent extends BaseComponent implements OnInit {

  progressBarValue = 0;
  isLoading: boolean = false;
  questionList: DiscoveryChatQuestion[] = [];
  currentQuestionPos: number = 0;
  historicResponses: Map<string, PossibleResponse>;
  TOTAL_QUESTIONS_NUMBER = 66;
  currentQuestionSetId: string = "";
  lastQuestionSetId: string = "";
  presentConsultantQuestionsVideo : boolean = false;
  isEndingProcess :boolean = false;
  presentedGapStatement :boolean = false;
  ignoreNextResponse: boolean = false;
  moveToFinalStageWasPresented : boolean = false;

  // This is an hidden input that is allowing us to scroll to top - this is ugly, but Angular.
  @ViewChild('topAnchor', {static: false}) inputEl: ElementRef;

  // we need this in order to use the enum in HTML
  eDiscoveryQuestionTypeEnum = DiscoveryQuestionTypeEnum;

  constructor(private readonly discoveryProcessService: AbstractDiscoveryProcessService,
              private router: Router,
              public dialog: MatDialog,
              private readonly authService: AbstractAuthorizationService,
              private readonly personalUserService: AbstractPersonalUserDataService,
              private translate: TranslateService,
              private genderSerivce: GenderPipe) {
    super()
  }

  ngOnInit(): void {
    super.ngOnInit();

    console.log(`DBG in DW ngOnInit isMobileView is ${this.isMobileView}`);

    if (this.user) {
      if (this.user.discoveryStatus == DiscoveryStatusEnum.DONE){
          // the user has navigated here by mistake
          this.router.navigate([NAVIGATION_ROUTES.app.personalView]);
      }
      this.verifyPresentQuestionsVideos();

      this.discoveryProcessService.continueUserJourney().subscribe(response => {
          if (!response?.journeyId) {
              // journeyId is null — discovery is already finished
              this.router.navigate([NAVIGATION_ROUTES.app.personalView]);
              return;
          }
          this.TOTAL_QUESTIONS_NUMBER = response.totalQuestions || 66;
          this.discoveryProcessService.journeyGetQuestionsInBatch().subscribe(batchResponse => {
              if (batchResponse && batchResponse.length > 0) {
                this.questionList = sortBy(batchResponse, ['batchIdx']);
                this.currentQuestionSetId = this.questionList[this.currentQuestionPos].setId;
                this.setProgressBarValue();
              } else {
                // no questions left — discovery is done
                this.router.navigate([NAVIGATION_ROUTES.app.personalView]);
              }
          });
      });
    }
  }

//   setQuestionResponse(responses DiscoveryResponse[])
  setQuestionResponse(responses : DiscoveryResponse[]) {
    if (this.isEndingProcess){
      this.showDone();
    }

    if (this.ignoreNextResponse){
      // how we handle statements
      this.showNextQuestion(1);
      this.ignoreNextResponse = false;

      return;
    }

    this.analytics.userRespondedToQuestioner();

    this.showLoading();

    // console.log (`DBG in DW setQuestionResponse currentQuestionPos is ${this.currentQuestionPos} questionList-length is ${this.questionList.length} reponses is ${JSON.stringify(responses)}`);

    let posQuestionId = this.questionList[this.currentQuestionPos].id;
    let negQuestionId = this.questionList[this.currentQuestionPos+1].id;

    let posResponseId = responses.find(currRes => currRes.responseType === ResponseStateEnum.Positive)?.responseId;
    if (!posResponseId){
        posResponseId = "";
    }

    let negResponseId = responses.find(currRes => currRes.responseType === ResponseStateEnum.Negative)?.responseId;
    if (!negResponseId){
        negResponseId = "";
    }

    let addMoreQuestionRequiredPreCheck = false;
    if (this.currentQuestionPos >= this.questionList.length- 2){
      // showNextQuestion can be called BEFORE forkjoin ends, causing us to skip questions (pos+2 happens twice) - so we have to check this condision prior to async action
      addMoreQuestionRequiredPreCheck = true;
    }

    forkJoin({
        posResult : this.discoveryProcessService.journeySetQuestionResponse(posQuestionId, [posResponseId]),
        negResult : this.discoveryProcessService.journeySetQuestionResponse(negQuestionId, [negResponseId])
    }).subscribe(({posResult, negResult })=>{
        if (addMoreQuestionRequiredPreCheck){
            // we need to get next batch, fot that we need to wait to set response to end
            // console.log (`DBG in DW setQuestionResponse in fork join`);
            this.showNextQuestion();
        }
    });

    if (this.currentQuestionPos < this.questionList.length- 2){
        // we only call show next question, no need to wait for set response to end
        // console.log (`DBG in DW setQuestionResponse outside of ForkJoin`);
        this.showNextQuestion();
    }
}

verifyPresentQuestionsVideos(): void{
  this.personalUserService.verifyFeature(FeatureID.discoveryConsultantQuestionsVideos).subscribe(res => {
    this.presentConsultantQuestionsVideo = res;
  });
}

setQuestionMotivationResponse(responses : DiscoveryResponse[]) {
  this.showLoading();

  let questionId = this.questionList[this.currentQuestionPos].id;

  let responseId = responses[0].responseId;

  let addMoreQuestionRequiredPreCheck = false;
  if (this.currentQuestionPos >= this.questionList.length- 1){
    // showNextQuestion can be called BEFORE forkjoin ends, causing us to skip questions (pos+2 happens twice) - so we have to check this condision prior to async action
    addMoreQuestionRequiredPreCheck = true;
  }

  this.discoveryProcessService.journeySetQuestionResponse(questionId, [responseId]).subscribe(result=>{
      if (addMoreQuestionRequiredPreCheck){
          // we need to get next batch, fot that we need to wait to set response to end
          this.showNextQuestion();
      }
    });

  if (this.currentQuestionPos < this.questionList.length- 1){
      // we only call show next question, no need to wait for set response to end
      this.showNextQuestion();
  }
}

setQuestionTailResponse(responses : DiscoveryResponse[]) {
  this.showLoading();

  let questionId = this.questionList[this.currentQuestionPos].id;

  let responsesIds: string[] = [];
  for (let currResponse of responses){
    responsesIds= responsesIds.concat(currResponse.responseId);
  }

  let addMoreQuestionRequiredPreCheck = false;
  if (this.currentQuestionPos >= this.questionList.length- 1){
    // showNextQuestion can be called BEFORE forkjoin ends, causing us to skip questions (pos+2 happens twice) - so we have to check this condision prior to async action
    addMoreQuestionRequiredPreCheck = true;
  }

  // console.log (`DBG DW in setQuestionTailResponse questionId is ${questionId} reponsesIds is ${JSON.stringify(responsesIds)}`);

  this.discoveryProcessService.journeySetQuestionResponse(questionId, responsesIds).subscribe(result=>{
      if (addMoreQuestionRequiredPreCheck){
          // we need to get next batch, fot that we need to wait to set response to end
          this.showNextQuestion();
      }
    });

  if (this.currentQuestionPos < this.questionList.length- 1){
      // we only call show next question, no need to wait for set response to end
      this.showNextQuestion();
  }
}

private async showNextQuestion(forceDelta : number = -1) {

  this.questionList[this.currentQuestionPos].isAnswered = true;
  let questionDelta = 1;

  if (forceDelta != -1){
    questionDelta = forceDelta;
  }
  else if (this.questionList[this.currentQuestionPos].type == DiscoveryQuestionTypeEnum.Standard){
    this.questionList[this.currentQuestionPos+1].isAnswered = true;
    questionDelta = 2;
  }

  if (this.currentQuestionPos < this.questionList.length- questionDelta) {
      this.currentQuestionPos+=questionDelta;
      this.setProgressBarValue();
      this.hideLoading();

      // scroll to top - this is ugly as hell, angular...
      setTimeout(() => this.inputEl.nativeElement.focus());
  } else {
      await this.getNextBatchOrDone();
  }
}

private async getNextBatchOrDone() {
  // console.log(`getting next batch`);
  let batchResponse = await  firstValueFrom(this.discoveryProcessService.journeyGetQuestionsInBatch());

  this.hideLoading();

  if (batchResponse && batchResponse.length > 0) {
      // console.log(`batch added ${batchResponse.length} questions`);
      batchResponse = sortBy(batchResponse, ['batchIdx']);

      if (!this.moveToFinalStageWasPresented && (batchResponse[0].batchId == BatchId.Gap || batchResponse[0].batchId == BatchId.Tail)){
        const questionText = this.translate.instant('discoveryProcess.moovToFinalStageStatement');
        const stageStatement : DiscoveryChatQuestion = {id:"moveToFinalStage", idx:0, batchId:BatchId.System, batchIdx:1, setId:"", setIdx:0, type:DiscoveryQuestionTypeEnum.Standard, userResponsesNo: 0, questionText:questionText, questionWizardText:questionText, possibleResponses:[]};
        this.questionList.push(stageStatement);
        this.ignoreNextResponse = true;
        this.moveToFinalStageWasPresented = true;
      }

      this.questionList = this.questionList.concat(batchResponse);
      // console.log(`after adding batch this.questionList.length: ${this.questionList.length}`);
      let questionDelta = 1;

      if (this.questionList[this.currentQuestionPos].type == DiscoveryQuestionTypeEnum.Standard){
        questionDelta = 2;
      }

      this.currentQuestionPos+=questionDelta;
      this.setProgressBarValue();

      this.scrollToTop();
      } else {
      if (!this.isEndingProcess){
        this.createEndingStatement();
        this.isEndingProcess = true;
      }
  }
}

private scrollToTop(){
      // scroll to top - this is ugly as hell, angular...
      setTimeout(() => this.inputEl.nativeElement.focus());

}

private showDone() {


  this.analytics.discoveryProcessCompletedEvent();

  this.authService.updateLoggedInUserToken().subscribe(res => {
      const dialogRef = this.dialog.open(DoneDiscoveryProcessComponent, {
          height: '40%',
          width: '60%'
      });

      dialogRef.afterClosed().subscribe(res => {
          this.router.navigate([NAVIGATION_ROUTES.app.personalView]);
      });
  });
  }
// called after every move, so the bar reflects the question on screen
setProgressBarValue(){
    const value = getDiscoveryProgressValue(this.questionList, this.currentQuestionPos, this.TOTAL_QUESTIONS_NUMBER);
    if (value !== null) {
      this.progressBarValue = value;
    }
  }

  createEndingStatement(): void{
    let firstQuestionResposnes: PossibleResponse[] = [];
    const questionId = "endingStatement"
    let idx = 1;
    // create possible responses

    // const questionText = this.translate.instant(this.genderSerivce.transform('rediscoverProcess.introText'))
    const questionText = this.translate.instant('discoveryProcess.endingStatement');
    const firstQuestion : DiscoveryChatQuestion = {
      id:questionId, idx:0, batchId:"", batchIdx:1, setId:"", setIdx:0,
      type:DiscoveryQuestionTypeEnum.Standard, userResponsesNo: 0,
      questionText:questionText, questionWizardText:questionText,
      possibleResponses:[]
    };

    this.questionList.push(firstQuestion);

    let questionDelta = 1;

    if (this.questionList[this.currentQuestionPos].type == DiscoveryQuestionTypeEnum.Standard){
      questionDelta = 2;
    }

    this.currentQuestionPos+=questionDelta;
    this.setProgressBarValue();
    this.scrollToTop();
}

  private showLoading() {
    this.isLoading = true;
  }

  private hideLoading() {
    this.isLoading = false;
  }
}
